import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, MinusIcon, PlusIcon, ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';

const MIN_PLAYERS = 2;
const MAX_PLAYERS = 8;

const GameToolsPage: React.FC = () => {
  const navigate = useNavigate();
  const [playerCount, setPlayerCount] = useState(4);

  const launchFirstPlayer = () => {
    const teams = Array.from({ length: playerCount }, (_, i) => ({
      teamName: `Player ${i + 1}`,
      playerNames: [`Player ${i + 1}`],
    }));
    navigate('/tools/first-player', { state: { teams } });
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="relative pt-14 sm:pt-4 pb-4 px-4">
        <button
          onClick={() => navigate(-1)}
          className="absolute left-4 top-14 sm:top-4 p-2 -ml-2 text-body hover:text-heading"
        >
          <ArrowLeftIcon className="h-5 w-5" />
        </button>
        <h1 className="text-fluid-h2 font-bold text-heading text-center">Game Tools</h1>
      </div>

      <div className="max-w-lg mx-auto px-4 space-y-6 pb-8">
        {/* Game Picker */}
        <div className="bg-surface rounded-2xl shadow-sm border border-edge overflow-hidden">
          <button
            data-track="tool_game_picker"
            onClick={() => navigate('/tools/game-picker')}
            className="w-full px-5 py-4 flex items-center justify-between hover:bg-surface-hover transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center text-xl">
                🎲
              </div>
              <div className="text-left">
                <div className="font-semibold text-heading">Game Picker</div>
                <div className="text-sm text-muted">Randomly pick a game to play</div>
              </div>
            </div>
            <ArrowLeftIcon className="h-4 w-4 text-disabled rotate-180" />
          </button>
        </div>

        {/* Coin Toss */}
        <div className="bg-surface rounded-2xl shadow-sm border border-edge overflow-hidden">
          <button
            data-track="tool_coin_toss"
            onClick={() => navigate('/tools/coin-toss')}
            className="w-full px-5 py-4 flex items-center justify-between hover:bg-surface-hover transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center text-xl">
                🪙
              </div>
              <div className="text-left">
                <div className="font-semibold text-heading">Coin Toss</div>
                <div className="text-sm text-muted">Flip a coin to decide</div>
              </div>
            </div>
            <ArrowLeftIcon className="h-4 w-4 text-disabled rotate-180" />
          </button>
        </div>

        {/* Rules Chat */}
        <div className="bg-surface rounded-2xl shadow-sm border border-edge overflow-hidden">
          <button
            data-track="tool_rules_chat"
            onClick={() => navigate('/tools/rules-chat')}
            className="w-full px-5 py-4 flex items-center justify-between hover:bg-surface-hover transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <ChatBubbleLeftRightIcon className="h-5 w-5 text-green-600" />
              </div>
              <div className="text-left">
                <div className="font-semibold text-heading">Rules Chat</div>
                <div className="text-sm text-muted">Ask AI about any game's rules</div>
              </div>
            </div>
            <ArrowLeftIcon className="h-4 w-4 text-disabled rotate-180" />
          </button>
        </div>

        {/* Dice Roller */}
        <div className="bg-surface rounded-2xl shadow-sm border border-edge overflow-hidden">
          <button
            data-track="tool_dice_roller"
            onClick={() => navigate('/tools/dice-roller')}
            className="w-full px-5 py-4 flex items-center justify-between hover:bg-surface-hover transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center text-xl">
                🎲
              </div>
              <div className="text-left">
                <div className="font-semibold text-heading">Dice Roller</div>
                <div className="text-sm text-muted">Roll 1–6 dice</div>
              </div>
            </div>
            <ArrowLeftIcon className="h-4 w-4 text-disabled rotate-180" />
          </button>
        </div>

        {/* First Player */}
        <div className="bg-surface rounded-2xl shadow-sm border border-edge overflow-hidden">
          <div className="px-5 py-4 border-b border-edge-subtle">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                <img src="/images/meeple.svg" alt="meeple" className="w-6 h-6 opacity-70" />
              </div>
              <div>
                <div className="font-semibold text-heading">First Player</div>
                <div className="text-sm text-muted">Pick who goes first with a minigame</div>
              </div>
            </div>
          </div>

          <div className="px-5 py-4 flex items-center justify-between">
            <span className="text-sm font-medium text-body">Number of players</span>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setPlayerCount((c) => Math.max(MIN_PLAYERS, c - 1))}
                disabled={playerCount <= MIN_PLAYERS}
                className="w-8 h-8 rounded-full flex items-center justify-center border border-edge-input text-body hover:bg-surface-active disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <MinusIcon className="h-4 w-4" />
              </button>
              <span className="text-lg font-bold text-heading w-6 text-center">{playerCount}</span>
              <button
                onClick={() => setPlayerCount((c) => Math.min(MAX_PLAYERS, c + 1))}
                disabled={playerCount >= MAX_PLAYERS}
                className="w-8 h-8 rounded-full flex items-center justify-center border border-edge-input text-body hover:bg-surface-active disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <PlusIcon className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="px-5 py-3 border-t border-edge-subtle">
            <button
              data-track="tool_first_player_start"
              onClick={launchFirstPlayer}
              className="w-full py-2.5 px-4 rounded-xl font-semibold text-sm bg-primary-600 hover:bg-primary-700 text-white transition-colors"
            >
              Start
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GameToolsPage;
